import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { logsApi, LogEntry, LogsResponse } from "../services/logsApi";
import {
  Search,
  AlertCircle,
  Info,
  AlertTriangle,
  Bug,
  ChevronDown,
  ChevronUp,
  // Trash2,
  RefreshCw,
  Filter,
  Layers,
} from "lucide-react";
import CustomSelect from "./ui/CustomSelect";
import type { SelectOption } from "./ui/CustomSelect";
import Skeleton, { SkeletonTable } from "./ui/Skeleton";

// Purple theme tokens matching Layout/Dashboard
const t = {
  primary: "#7c3aed",
  primaryLight: "#ede9fe",
  surface: "#ffffff",
  border: "#e9e5f5",
  danger: "#ef4444",
  dangerBg: "#fee2e2",
  warn: "#f59e0b",
  warnBg: "#fef3c7",
  text: { primary: "#1e1b4b", secondary: "#6b7280", muted: "#9ca3b8" },
};

const LEVEL_STYLE: Record<
  string,
  { bg: string; color: string; Icon: React.ElementType }
> = {
  error: { bg: t.dangerBg, color: t.danger, Icon: AlertCircle },
  warn: { bg: t.warnBg, color: t.warn, Icon: AlertTriangle },
  info: { bg: t.primaryLight, color: t.primary, Icon: Info },
  debug: { bg: "#f1f5f9", color: "#475569", Icon: Bug },
};

export default function LogsTable() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlQuery = searchParams.get("q") || "";

  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [groups, setGroups] = useState<
    Array<{
      userId: string;
      userName?: string;
      totalLogs: number;
      firstLogTimestamp?: string;
      levels: Array<{ key: string; doc_count: number }>;
      services: Array<{ key: string; doc_count: number }>;
      logs: LogEntry[];
    }>
  >([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [expandedLog, setExpandedLog] = useState<string | null>(null);
  const [expandedUser, setExpandedUser] = useState<string | null>(null);
  const [isGroupedView, setIsGroupedView] = useState(false);
  const [userLogsPage, setUserLogsPage] = useState<Record<string, number>>({});
  const USER_LOGS_PAGE_SIZE = 10;

  // Filters
  const [searchQuery, setSearchQuery] = useState(urlQuery);
  const [levelFilter, setLevelFilter] = useState("");
  const [serviceFilter, setServiceFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [services, setServices] = useState<
    Array<{ key: string; doc_count: number }>
  >([]);
  const [initialLoad, setInitialLoad] = useState(true);

  // Filters applied on "Appliquer" click – kept separate from live input state
  const [appliedFilters, setAppliedFilters] = useState({
    q: urlQuery,
    level: "",
    service: "",
    startDate: "",
    endDate: "",
  });

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const response: LogsResponse = await logsApi.getLogs({
        q: appliedFilters.q || undefined,
        level: appliedFilters.level || undefined,
        service: appliedFilters.service || undefined,
        excludeService: "logs-api",
        startDate: appliedFilters.startDate || undefined,
        endDate: appliedFilters.endDate || undefined,
        page,
        limit: 50,
        sort: "desc",
        groupBy: isGroupedView ? "userId" : undefined,
      });
      if (response.success) {
        if (
          response.data.grouped &&
          response.data.groups &&
          response.data.groups.length > 0
        ) {
          setGroups(response.data.groups);
          setLogs([]); // Clear regular logs when in grouped mode
        } else if (response.data.logs) {
          setLogs(response.data.logs);
          setGroups([]); // Clear groups when in normal mode
        } else {
          // Handle case where grouped is true but no groups found
          setGroups([]);
          setLogs([]);
        }
        setTotal(response.data.total);
        setPages(response.data.pages);
      }
    } catch (error) {
      console.error("Failed to load logs:", error);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, page, isGroupedView]);

  const loadServices = useCallback(async () => {
    try {
      // Pass current filters EXCEPT `service` so the dropdown reflects the
      // active query/level/date selection without removing the other services
      // the user could switch to.
      const response = await logsApi.getServices({
        q: appliedFilters.q || undefined,
        level: appliedFilters.level || undefined,
        startDate: appliedFilters.startDate || undefined,
        endDate: appliedFilters.endDate || undefined,
      });
      if (response.success) setServices(response.data);
    } catch (error) {
      console.error("Failed to load services:", error);
    }
  }, [appliedFilters]);

  useEffect(() => {
    loadLogs().then(() => setInitialLoad(false));
  }, [loadLogs]);

  useEffect(() => {
    loadServices();
  }, [loadServices]);

  const handleSearch = () => {
    setPage(1);
    setAppliedFilters({
      q: searchQuery,
      level: levelFilter,
      service: serviceFilter,
      startDate,
      endDate,
    });
    // Keep URL in sync
    if (searchQuery) {
      setSearchParams({ q: searchQuery });
    } else {
      setSearchParams({});
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSearch();
  };

  const handleReset = () => {
    setSearchQuery("");
    setLevelFilter("");
    setServiceFilter("");
    setStartDate("");
    setEndDate("");
    setPage(1);
    setAppliedFilters({
      q: "",
      level: "",
      service: "",
      startDate: "",
      endDate: "",
    });
    setExpandedLog(null);
    setExpandedUser(null);
    setUserLogsPage({});
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cet appel ?"))
      return;
    try {
      await logsApi.deleteLog(id);
      loadLogs();
    } catch (error) {
      console.error("Failed to delete log:", error);
    }
  };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });

  const inputStyle: React.CSSProperties = {
    border: `1px solid ${t.border}`,
    color: t.text.secondary,
    backgroundColor: t.surface,
    minHeight: "40px",
  };

  const levelOptions: SelectOption[] = [
    { value: "", label: "Tous", dividerAfter: true },
    { value: "error", label: "Error" },
    { value: "warn", label: "Warn" },
    { value: "info", label: "Info" },
    { value: "debug", label: "Debug" },
  ];

  const serviceOptions: SelectOption[] = [
    { value: "", label: "Tous", dividerAfter: true },
    ...services.map((s) => ({
      value: s.key,
      label: s.key,
      count: s.doc_count,
    })),
  ];

  if (initialLoad) {
    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <Skeleton width={260} height={26} className="mb-2" />
            <Skeleton width={180} height={14} />
          </div>
          <Skeleton width={120} height={40} rounded="xl" />
        </div>
        <div
          className="rounded-2xl p-5 mb-5"
          style={{
            backgroundColor: t.surface,
            border: `1px solid ${t.border}`,
          }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i}>
                <Skeleton width={60} height={12} className="mb-2" />
                <Skeleton height={40} className="w-full" rounded="xl" />
              </div>
            ))}
          </div>
          <div className="flex gap-3 mt-4">
            <Skeleton width={90} height={36} rounded="xl" />
            <Skeleton width={100} height={36} rounded="xl" />
          </div>
        </div>
        <SkeletonTable rows={8} />
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      {/* Page title */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2
            className="text-[22px] font-bold"
            style={{ color: t.text.primary }}
          >
            Explorateur d'Appels API
          </h2>
          <p className="text-[13px] mt-0.5" style={{ color: t.text.secondary }}>
            {total.toLocaleString()}{" "}
            {isGroupedView
              ? total === 1
                ? "utilisateur"
                : "utilisateurs"
              : total === 1
                ? "appel"
                : "appels"}{" "}
            enregistré{total > 1 ? "s" : ""}
            {searchQuery && <span> · Filtre : « {searchQuery} »</span>}
            {isGroupedView && <span> · Vue regroupée par utilisateur</span>}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              setIsGroupedView(!isGroupedView);
              setPage(1);
            }}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-medium transition-all duration-200"
            style={{
              backgroundColor: isGroupedView ? t.primary : t.primaryLight,
              color: isGroupedView ? "#fff" : t.primary,
              border: "none",
              minHeight: "40px",
            }}
          >
            <Layers className="w-4 h-4" />
            {isGroupedView ? "Vue normale" : "Regrouper par utilisateur"}
          </button>
          <button
            onClick={loadLogs}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-medium text-white transition-all duration-200"
            style={{
              backgroundColor: t.primary,
              minHeight: "40px",
              border: "none",
            }}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Actualiser
          </button>
        </div>
      </div>

      {/* Filters card */}
      <div
        className="rounded-2xl p-5 mb-5"
        style={{ backgroundColor: t.surface, border: `1px solid ${t.border}` }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Search */}
          <div>
            <label
              htmlFor="searchQuery"
              className="block text-[12px] font-medium mb-1.5"
              style={{ color: t.text.secondary }}
            >
              Recherche
            </label>
            <div className="relative h-10 flex items-center">
              <Search
                className="absolute left-3 w-4 h-4 pointer-events-none"
                style={{ color: t.text.muted }}
              />
              <input
                id="searchQuery"
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                placeholder="Message, service..."
                className="w-full h-full pl-9 pr-3 rounded-lg text-[12px]"
                style={{
                  ...inputStyle,
                  color: t.text.primary,
                  lineHeight: "1.5",
                }}
              />
            </div>
          </div>

          {/* Level */}
          <div>
            <label
              className="block text-[12px] font-medium mb-1.5"
              style={{ color: t.text.secondary }}
            >
              Niveau
            </label>
            <CustomSelect
              value={levelFilter}
              onChange={setLevelFilter}
              placeholder="Tous"
              icon={<Filter className="w-3.5 h-3.5" />}
              options={levelOptions}
            />
          </div>

          {/* Service */}
          <div>
            <label
              className="block text-[12px] font-medium mb-1.5"
              style={{ color: t.text.secondary }}
            >
              Service
            </label>
            <CustomSelect
              value={serviceFilter}
              onChange={setServiceFilter}
              placeholder="Tous"
              icon={<Layers className="w-3.5 h-3.5" />}
              options={serviceOptions}
            />
          </div>

          {/* Start date */}
          <div>
            <label
              htmlFor="startDate"
              className="block text-[12px] font-medium mb-1.5"
              style={{ color: t.text.secondary }}
            >
              Depuis
            </label>
            <input
              id="startDate"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 rounded-xl text-[12px]"
              style={inputStyle}
            />
          </div>

          {/* End date */}
          <div>
            <label
              htmlFor="endDate"
              className="block text-[12px] font-medium mb-1.5"
              style={{ color: t.text.secondary }}
            >
              Jusqu'à
            </label>
            <input
              id="endDate"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 rounded-xl text-[12px]"
              style={inputStyle}
            />
          </div>
        </div>
        <div className="flex gap-3 mt-4">
          <button
            onClick={handleSearch}
            className="px-5 py-2 rounded-lg text-[12px] font-medium text-white transition-all duration-200"
            style={{ backgroundColor: t.primary, border: "none" }}
          >
            Appliquer
          </button>
          <button
            onClick={handleReset}
            className="px-5 py-2 rounded-lg text-[12px] font-medium transition-all duration-200"
            style={{
              backgroundColor: t.primaryLight,
              color: t.primary,
              border: "none",
            }}
          >
            Réinitialiser
          </button>
        </div>
      </div>
      {/* User cards in grouped view */}
      {isGroupedView && groups && groups.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-5">
          {groups.map((group) => (
            <div
              key={group.userId}
              className="rounded-xl p-4 cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-[1.02]"
              style={{
                backgroundColor: t.surface,
                border: `1px solid ${t.border}`,
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              }}
              onClick={() =>
                setExpandedUser(
                  expandedUser === group.userId ? null : group.userId,
                )
              }
            >
              {/* User avatar and name */}
              <div className="flex items-center gap-3 mb-3">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold text-white"
                  style={{ backgroundColor: t.primary }}
                >
                  {group.userId.charAt(0)?.toUpperCase() || "U"}
                </div>
                <div className="flex-1 min-w-0">
                  <div
                    className="text-[14px] font-semibold truncate"
                    style={{ color: t.text.primary }}
                  >
                    {group.userId === "unknown"
                      ? "Utilisateur inconnu"
                      : group.userName || group.userId}
                  </div>
                  <div
                    className="text-[11px]"
                    style={{ color: t.text.secondary }}
                  >
                    {group.totalLogs} requêtes
                  </div>
                </div>
              </div>

              {/* Creation date */}
              {group.firstLogTimestamp && (
                <div className="mb-3">
                  <div
                    className="text-[10px] font-medium uppercase tracking-wide"
                    style={{ color: t.text.muted }}
                  >
                    Première activité
                  </div>
                  <div
                    className="text-[11px]"
                    style={{ color: t.text.secondary }}
                  >
                    {new Date(group.firstLogTimestamp).toLocaleDateString(
                      "fr-FR",
                      {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      },
                    )}
                  </div>
                </div>
              )}

              {/* Level distribution */}
              <div className="mb-3">
                <div
                  className="text-[10px] font-medium uppercase tracking-wide mb-1"
                  style={{ color: t.text.muted }}
                >
                  Distribution
                </div>
                <div className="flex flex-wrap gap-1">
                  {group.levels.slice(0, 4).map((level) => {
                    const ls = LEVEL_STYLE[level.key] || LEVEL_STYLE.info;
                    const LevelIcon = ls.Icon;
                    return (
                      <span
                        key={level.key}
                        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-medium"
                        style={{ backgroundColor: ls.bg, color: ls.color }}
                      >
                        <LevelIcon className="w-2.5 h-2.5" />
                        {level.doc_count}
                      </span>
                    );
                  })}
                  {group.levels.length > 4 && (
                    <span
                      className="text-[9px]"
                      style={{ color: t.text.muted }}
                    >
                      +{group.levels.length - 4}
                    </span>
                  )}
                </div>
              </div>

              {/* Services count */}
              <div>
                <div
                  className="text-[10px] font-medium uppercase tracking-wide mb-1"
                  style={{ color: t.text.muted }}
                >
                  Services
                </div>
                <div
                  className="text-[11px]"
                  style={{ color: t.text.secondary }}
                >
                  {group.services.length} service
                  {group.services.length > 1 ? "s" : ""}
                </div>
              </div>

              {/* Expand indicator */}
              <div
                className="flex justify-center mt-3 pt-2 border-t"
                style={{ borderColor: t.border }}
              >
                <ChevronDown
                  className={`w-3 h-3 transition-transform ${expandedUser === group.userId ? "rotate-180" : ""}`}
                  style={{ color: t.text.muted }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Table card */}
      <div
        className="rounded-2xl overflow-hidden"
        style={{ backgroundColor: t.surface, border: `1px solid ${t.border}` }}
      >
        {isGroupedView ? (
          // Grouped view by user
          <div>
            {groups?.map((group) => (
              <div
                key={group.userId}
                className="border-b"
                style={{ borderColor: t.border }}
              >
                {/* User header */}
                <div
                  className="px-5 py-3 cursor-pointer transition-colors hover:bg-[#faf8ff]"
                  onClick={() =>
                    setExpandedUser(
                      expandedUser === group.userId ? null : group.userId,
                    )
                  }
                  style={{
                    backgroundColor:
                      expandedUser === group.userId ? "#faf8ff" : "transparent",
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold text-white"
                          style={{ backgroundColor: t.primary }}
                        >
                          {group.userId.charAt(0)?.toUpperCase() || "U"}
                        </div>
                        <div>
                          <div
                            className="text-[14px] font-semibold"
                            style={{ color: t.text.primary }}
                          >
                            {group.userId === "unknown"
                              ? "Utilisateur inconnu"
                              : group.userName || group.userId}
                          </div>
                          <div
                            className="text-[11px]"
                            style={{ color: t.text.secondary }}
                          >
                            {group.totalLogs} appels
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {group.levels.slice(0, 3).map((level) => {
                          const ls = LEVEL_STYLE[level.key] || LEVEL_STYLE.info;
                          const LevelIcon = ls.Icon;
                          return (
                            <span
                              key={level.key}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
                              style={{
                                backgroundColor: ls.bg,
                                color: ls.color,
                              }}
                            >
                              <LevelIcon className="w-3 h-3" />
                              {level.key}: {level.doc_count}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <ChevronDown
                        className={`w-4 h-4 transition-transform ${expandedUser === group.userId ? "rotate-180" : ""}`}
                        style={{ color: t.text.muted }}
                      />
                    </div>
                  </div>
                </div>

                {/* User logs */}
                {expandedUser === group.userId && (
                  <div className="px-5 pb-3">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr style={{ borderBottom: `1px solid ${t.border}` }}>
                            <th
                              className="px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-wide"
                              style={{ color: t.text.muted }}
                            >
                              Niveau
                            </th>
                            <th
                              className="px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-wide"
                              style={{ color: t.text.muted }}
                            >
                              Timestamp
                            </th>
                            <th
                              className="px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-wide"
                              style={{ color: t.text.muted }}
                            >
                              Service
                            </th>
                            <th
                              className="px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-wide"
                              style={{ color: t.text.muted }}
                            >
                              Message
                            </th>
                            <th
                              className="px-2 py-2 text-right text-[10px] font-semibold uppercase tracking-wide"
                              style={{ color: t.text.muted }}
                            >
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const currentPage = userLogsPage[group.userId] || 1;
                            const startIdx = (currentPage - 1) * USER_LOGS_PAGE_SIZE;
                            return group.logs.slice(startIdx, startIdx + USER_LOGS_PAGE_SIZE);
                          })().map((log) => {
                            const ls =
                              LEVEL_STYLE[log.level] || LEVEL_STYLE.info;
                            const LevelIcon = ls.Icon;
                            return (
                              <React.Fragment key={log.id}>
                                <tr
                                  className="cursor-pointer transition-colors hover:bg-[#faf8ff]"
                                  onClick={() =>
                                    setExpandedLog(
                                      expandedLog === log.id
                                        ? null
                                        : log.id || null,
                                    )
                                  }
                                  style={{
                                    borderBottom: `1px solid ${t.border}`,
                                  }}
                                >
                                  <td className="px-2 py-2 whitespace-nowrap">
                                    <span
                                      className="inline-flex items-center gap-1 px-1 py-0.5 rounded-full text-[9px] font-semibold"
                                      style={{
                                        backgroundColor: ls.bg,
                                        color: ls.color,
                                      }}
                                    >
                                      <LevelIcon className="w-2.5 h-2.5" />
                                      {log.level}
                                    </span>
                                  </td>
                                  <td
                                    className="px-2 py-2 whitespace-nowrap text-[10px]"
                                    style={{ color: t.text.secondary }}
                                  >
                                    {formatDate(log.timestamp)}
                                  </td>
                                  <td
                                    className="px-2 py-2 whitespace-nowrap text-[10px] font-medium"
                                    style={{ color: t.text.primary }}
                                  >
                                    {log.service}
                                  </td>
                                  <td
                                    className="px-2 py-2 text-[10px] max-w-xs truncate"
                                    style={{ color: t.text.secondary }}
                                  >
                                    {log.message}
                                  </td>
                                  <td className="px-2 py-2 whitespace-nowrap text-right">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setExpandedLog(
                                          expandedLog === log.id
                                            ? null
                                            : log.id || null,
                                        );
                                      }}
                                      className="p-1 rounded-md transition-colors hover:bg-[#ede9fe]"
                                      style={{
                                        color: t.text.muted,
                                        background: "transparent",
                                        border: "none",
                                      }}
                                      aria-label={
                                        expandedLog === log.id
                                          ? "Réduire"
                                          : "Développer"
                                      }
                                    >
                                      {expandedLog === log.id ? (
                                        <ChevronUp className="w-3 h-3" />
                                      ) : (
                                        <ChevronDown className="w-3 h-3" />
                                      )}
                                    </button>
                                  </td>
                                </tr>
                                {expandedLog === log.id && (
                                  <tr>
                                    <td
                                      colSpan={5}
                                      className="px-2 py-2"
                                      style={{ backgroundColor: "#faf8ff" }}
                                    >
                                      <pre
                                        className="text-[10px] overflow-x-auto"
                                        style={{ color: t.text.secondary }}
                                      >
                                        {JSON.stringify(log, null, 2)}
                                      </pre>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                      {group.logs.length > USER_LOGS_PAGE_SIZE && (() => {
                        const currentPage = userLogsPage[group.userId] || 1;
                        const totalPages = Math.ceil(group.logs.length / USER_LOGS_PAGE_SIZE);
                        const startIdx = (currentPage - 1) * USER_LOGS_PAGE_SIZE;
                        const endIdx = Math.min(startIdx + USER_LOGS_PAGE_SIZE, group.logs.length);
                        return (
                          <div
                            className="flex items-center justify-between px-2 py-2 mt-1"
                            style={{ borderTop: `1px solid ${t.border}` }}
                          >
                            <span className="text-[10px]" style={{ color: t.text.muted }}>
                              {startIdx + 1}–{endIdx} sur {group.logs.length}
                            </span>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setUserLogsPage((p) => ({ ...p, [group.userId]: Math.max(1, currentPage - 1) }));
                                  setExpandedLog(null);
                                }}
                                disabled={currentPage === 1}
                                className="w-7 h-7 flex items-center justify-center rounded-md text-[12px] font-bold transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                                style={{ color: t.primary, background: 'transparent', border: 'none' }}
                                aria-label="Page précédente"
                              >
                                ‹
                              </button>
                              {(() => {
                                const pageNums: (number | '...')[] = [];
                                if (totalPages <= 5) {
                                  for (let i = 1; i <= totalPages; i++) pageNums.push(i);
                                } else {
                                  pageNums.push(1);
                                  if (currentPage > 3) pageNums.push('...');
                                  for (
                                    let i = Math.max(2, currentPage - 1);
                                    i <= Math.min(totalPages - 1, currentPage + 1);
                                    i++
                                  )
                                    pageNums.push(i);
                                  if (currentPage < totalPages - 2) pageNums.push('...');
                                  pageNums.push(totalPages);
                                }
                                return pageNums.map((p, i) =>
                                  p === '...' ? (
                                    <span
                                      key={`dots-${i}`}
                                      className="w-7 h-7 flex items-center justify-center text-[10px]"
                                      style={{ color: t.text.muted }}
                                    >
                                      …
                                    </span>
                                  ) : (
                                    <button
                                      key={p}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setUserLogsPage((prev) => ({ ...prev, [group.userId]: p as number }));
                                        setExpandedLog(null);
                                      }}
                                      className="w-7 h-7 flex items-center justify-center rounded-md text-[11px] font-semibold transition-all"
                                      style={{
                                        backgroundColor: p === currentPage ? t.primary : 'transparent',
                                        color: p === currentPage ? '#ffffff' : t.primary,
                                        border: 'none',
                                      }}
                                    >
                                      {p}
                                    </button>
                                  ),
                                );
                              })()}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setUserLogsPage((p) => ({ ...p, [group.userId]: Math.min(totalPages, currentPage + 1) }));
                                  setExpandedLog(null);
                                }}
                                disabled={currentPage === totalPages}
                                className="w-7 h-7 flex items-center justify-center rounded-md text-[12px] font-bold transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                                style={{ color: t.primary, background: 'transparent', border: 'none' }}
                                aria-label="Page suivante"
                              >
                                ›
                              </button>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                )}
              </div>
            ))}
            {(!groups || groups.length === 0) && (
              <div
                className="py-12 text-center text-[13px]"
                style={{ color: t.text.muted }}
              >
                Aucun utilisateur trouvé
              </div>
            )}
          </div>
        ) : (
          // Normal view
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ borderBottom: `1px solid ${t.border}` }}>
                  <th
                    className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wide"
                    style={{ color: t.text.muted }}
                  >
                    Niveau
                  </th>
                  <th
                    className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wide"
                    style={{ color: t.text.muted }}
                  >
                    Timestamp
                  </th>
                  <th
                    className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wide"
                    style={{ color: t.text.muted }}
                  >
                    Service
                  </th>
                  <th
                    className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wide"
                    style={{ color: t.text.muted }}
                  >
                    Utilisateur
                  </th>
                  <th
                    className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wide"
                    style={{ color: t.text.muted }}
                  >
                    Message
                  </th>
                  <th
                    className="px-5 py-3 text-right text-[11px] font-semibold uppercase tracking-wide"
                    style={{ color: t.text.muted }}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => {
                  const ls = LEVEL_STYLE[log.level] || LEVEL_STYLE.info;
                  const LevelIcon = ls.Icon;
                  return (
                    <React.Fragment key={log.id}>
                      <tr
                        className="cursor-pointer transition-colors hover:bg-[#faf8ff]"
                        onClick={() =>
                          setExpandedLog(
                            expandedLog === log.id ? null : log.id || null,
                          )
                        }
                        style={{ borderBottom: `1px solid ${t.border}` }}
                      >
                        <td className="px-5 py-3 whitespace-nowrap">
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
                            style={{ backgroundColor: ls.bg, color: ls.color }}
                          >
                            <LevelIcon className="w-3 h-3" />
                            {log.level}
                          </span>
                        </td>
                        <td
                          className="px-5 py-3 whitespace-nowrap text-[12px]"
                          style={{ color: t.text.secondary }}
                        >
                          {formatDate(log.timestamp)}
                        </td>
                        <td
                          className="px-5 py-3 whitespace-nowrap text-[12px] font-medium"
                          style={{ color: t.text.primary }}
                        >
                          {log.service}
                        </td>
                        <td
                          className="px-5 py-3 whitespace-nowrap text-[12px]"
                          style={{ color: t.text.secondary }}
                        >
                          {log.userId ? (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium"
                              style={{
                                backgroundColor: t.primaryLight,
                                color: t.primary,
                              }}
                              title={log.userId}
                            >
                              {(() => {
                                const u = log.metadata?.user;
                                const obj = u && !Array.isArray(u) ? u : {};
                                const fullName = [obj.firstname, obj.lastname].filter(Boolean).join(' ');
                                return fullName || obj.email || log.userId;
                              })()}
                            </span>
                          ) : (
                            <span
                              className="text-[11px]"
                              style={{ color: t.text.muted }}
                            >
                              —
                            </span>
                          )}
                        </td>
                        <td
                          className="px-5 py-3 text-[12px] max-w-sm truncate"
                          style={{ color: t.text.secondary }}
                        >
                          {log.message}
                        </td>
                        <td className="px-5 py-3 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedLog(
                                  expandedLog === log.id
                                    ? null
                                    : log.id || null,
                                );
                              }}
                              className="p-1.5 rounded-md transition-colors hover:bg-[#ede9fe]"
                              style={{
                                color: t.text.muted,
                                background: "transparent",
                                border: "none",
                              }}
                              aria-label={
                                expandedLog === log.id
                                  ? "Réduire"
                                  : "Développer"
                              }
                            >
                              {expandedLog === log.id ? (
                                <ChevronUp className="w-4 h-4" />
                              ) : (
                                <ChevronDown className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                      {expandedLog === log.id && (
                        <tr>
                          <td
                            colSpan={6}
                            className="px-5 py-4"
                            style={{ backgroundColor: "#faf8ff" }}
                          >
                            <pre
                              className="text-[11px] overflow-x-auto"
                              style={{ color: t.text.secondary }}
                            >
                              {JSON.stringify(log, null, 2)}
                            </pre>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {logs.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      className="py-12 text-center text-[13px]"
                      style={{ color: t.text.muted }}
                    >
                      Aucun appel API trouvé
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        <div
          className="px-5 py-3 flex items-center justify-center gap-1"
          style={{ borderTop: `1px solid ${t.border}` }}
        >
          {/* Prev chevron */}
          <button
            onClick={() => setPage(page - 1)}
            disabled={page === 1}
            className="w-9 h-9 flex items-center justify-center rounded-lg text-[14px] font-bold transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
            style={{
              color: t.primary,
              background: "transparent",
              border: "none",
            }}
            aria-label="Page précédente"
          >
            ‹
          </button>

          {/* Page numbers */}
          {(() => {
            const pageNums: (number | "...")[] = [];
            if (pages <= 7) {
              for (let i = 1; i <= pages; i++) pageNums.push(i);
            } else {
              pageNums.push(1);
              if (page > 3) pageNums.push("...");
              for (
                let i = Math.max(2, page - 1);
                i <= Math.min(pages - 1, page + 1);
                i++
              )
                pageNums.push(i);
              if (page < pages - 2) pageNums.push("...");
              pageNums.push(pages);
            }
            return pageNums.map((p, i) =>
              p === "..." ? (
                <span
                  key={`dots-${i}`}
                  className="w-9 h-9 flex items-center justify-center text-[12px]"
                  style={{ color: t.text.muted }}
                >
                  …
                </span>
              ) : (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`w-9 h-9 flex items-center justify-center rounded-lg text-[13px] font-semibold transition-all duration-200`}
                  style={{
                    backgroundColor: p === page ? t.primary : "transparent",
                    color: p === page ? "#ffffff" : t.primary,
                    border: "none",
                    transform: p === page ? "scale(1.05)" : "scale(1)",
                  }}
                >
                  {p}
                </button>
              ),
            );
          })()}

          {/* Next chevron */}
          <button
            onClick={() => setPage(page + 1)}
            disabled={page === pages}
            className="w-9 h-9 flex items-center justify-center rounded-lg text-[14px] font-bold transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
            style={{
              color: t.primary,
              background: "transparent",
              border: "none",
            }}
            aria-label="Page suivante"
          >
            ›
          </button>
        </div>
      </div>
    </div>
  );
}
